import { Component } from 'react';
import lodash from 'lodash';
import uuid from 'uuid';
import styles from '../containers/Events.scss';

import {
    EventDetailsHeader,
    RequiresSignatureCheckbox,
    UserMultiSelect,
    DetailInput,
} from './EventDetailsControls';

import {
    EventTypeFieldsMap,
    formatItemBy,
} from './EventTypeFields';

import CustomEventFields from './CustomEventFields';

import InputField from 'pages/study/containers/inputFields/inputFields';

import { Button, BUTTON } from 'components/common/Button/Button';

const formatSignaturesForMultiselectCheckbox = items => items.map(({ requestedFrom, status }) => {
    const { id: requestUserId, displayName } = requestedFrom;
    return {
        id: requestUserId,
        value: requestUserId,
        label: displayName,
        status,
    };
});

function getEventTypeFromList(eventTypes, searchId) {
    if (eventTypes.length === 1) {
        return eventTypes[0];
    }
    return lodash.find(eventTypes, eventType => eventType.displayName === searchId);
}

const updateableFields = {
    'monitorVisit': ['monitorId', 'monitorVisitType', 'dateOfVisit'],
    'icfVersionChange': [
        'irbApprovalDate',
        'icfType',
        'icfLanguage',
        'reconsentRequired',
        'sponsorVersion',
        'siteVersion',
        'protocolVersion',
        'summaryOfChanges',
    ]
};

export class EventDetailsPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            details: {
                signatureRequests: [],
                signatureRequestsToCancel: [],
            },
            requiresSignature: false,
            isEdited: false,
            editNoteCanBeProvided: false,
            eventType: {},
        };
    }
    componentDidMount() {
        this.setActiveEventFromProps();
    }

    setActiveEventFromProps() {
        const { activeEvent, eventTypes } = this.props;

        if (lodash.isEmpty(activeEvent)) {
            return null;
        }

        const activeEventTypeName = activeEvent.eventType.displayName || activeEvent.eventType;

        const eventType = getEventTypeFromList(eventTypes, activeEventTypeName);

        const { signatureRequests = [] } = activeEvent;
        const formattedSignatureRequests = signatureRequests.reduce((acc, signatureRequest) => {
            return acc.concat({
                id: lodash.get(signatureRequest, 'requestedFrom.id'),
                label: lodash.get(signatureRequest, 'requestedFrom.displayName'),
                status: lodash.get(signatureRequest, 'status')
            });
        }, []);

        const hasPendingSignatureRequests = activeEvent.signatureRequestPending > 0;
        const hasFormattedSignatureRequests = formattedSignatureRequests.length > 0;
        const requiresSignature = hasPendingSignatureRequests || hasFormattedSignatureRequests;
        this.setState({ requiresSignature });

        const details = {
            ...activeEvent,
            eventType,
            signatureRequests: formattedSignatureRequests,
            signatureRequestsToCancel: [],
        };

        this.setState({
            details,
            updateMode: !lodash.isEmpty(activeEvent),
        });

        if (lodash.isEmpty(activeEvent)) {
            return;
        }

        const pendingSignatureRequests = signatureRequests.filter(request => request.status === 'PENDING');
        this.setState({
            pendingSignatureRequests,
        });
    }

    componentDidUpdate(previousProps, previousState) {
        if (!lodash.isEqual(previousProps.activeEvent, this.props.activeEvent)) {
            this.setActiveEventFromProps();
        }
    }

    saveEvent() {

        const {
            details,
        } = this.state;
        this.props.onSubmit(details.eventType.id, details);
    }

    setFieldValue(name, value) {
        const { details } = this.state;

        let canceledSignatureRestored = false;
        if (name === 'signatureRequests') {
            canceledSignatureRestored = this.updateCanceledSignatureRequests(value);
        }

        if (!canceledSignatureRestored) {
            details[name] = value;
            this.setState({
                details,
            });
        }

        this.onEditHandler();
    }

    updateCanceledSignatureRequests(signatureRequests) {
        if (lodash.isEmpty(this.state.pendingSignatureRequests)) {
            return false;
        }

        const signatureRequestFromIds = signatureRequests.map(request => request.value);
        const matchingPendingSignatures = this.state.pendingSignatureRequests.filter(req => signatureRequestFromIds.includes(req.requestedFrom.id));
        const matchingPendingSignatureTaskIds = matchingPendingSignatures.map(req => req.id);

        if (lodash.isEmpty(matchingPendingSignatureTaskIds)) {
            return false;
        }

        const updatedSignatureRequestsToCancel = this.state.details.signatureRequestsToCancel.filter(taskId => !matchingPendingSignatureTaskIds.includes(taskId));
        const { details } = this.state;
        details['signatureRequestsToCancel'] = updatedSignatureRequestsToCancel;
        this.setState({
            details,
        });
        return true;
    }

    toggleRequiresSignatureTextBox() {
        const activeEventSignatureRequests = lodash.get(this.props, 'activeEvent.signatureRequests', []);
        if (activeEventSignatureRequests.length === 0) {
            const { requiresSignature } = this.state;
            this.setState({
                requiresSignature: !requiresSignature,
            });
            this.setFieldValue('signatureRequests', []);
            this.onEditHandler();
        }
    }

    onClose() {
        this.props.onCancel();
    }

    onEditHandler() {
        const currentEventDetails = this.state.details;
        const initialEventDetails = this.props.activeEvent;
        const isCustomEvent = currentEventDetails.eventType.name !== 'monitorVisit' && currentEventDetails.eventType.name !== 'icfVersionChange';

        if (lodash.isEmpty(initialEventDetails)) {
            return;
        }

        if (isCustomEvent && !currentEventDetails.fields) {
            return;
        }

        const signatureRequestsAdded = currentEventDetails.signatureRequests.length > 0;
        const signatureRequestsCanceled = currentEventDetails.signatureRequestsToCancel.length > 0;

        const keysForEventType = updateableFields[currentEventDetails.eventType.name] || currentEventDetails.fields.map(field => field.name);
        let initialKeys, currentKeys;

        if (isCustomEvent) {
            initialKeys = initialEventDetails.fields.filter(field => field.name !== 'editNote').map(field => {
                return {
                    [field.name]: field.value,
                };
            });

            currentKeys = currentEventDetails.fields.filter(field => field.name !== 'editNote').map(field => {
                return {
                    [field.name]: currentEventDetails[field.name] || field.value,
                };
            });
        } else {
            initialKeys = lodash.pick(initialEventDetails, keysForEventType);
            currentKeys = lodash.pick(currentEventDetails, keysForEventType);
        }

        const fieldsChanged = !lodash.isEqual(initialKeys, currentKeys);
        const currentKeysList = Object.keys(currentKeys);

        let savedFieldValuesChanged;

        if (isCustomEvent) {
            savedFieldValuesChanged = fieldsChanged;
        } else {
            savedFieldValuesChanged = currentKeysList.some(key => initialKeys[key] && currentKeys[key] !== initialKeys[key]);
        }

        const updateMode = lodash.isEmpty(initialEventDetails);

        this.setState({
            isEdited: fieldsChanged || signatureRequestsAdded || signatureRequestsCanceled || updateMode,
            editNoteCanBeProvided: this.props.updateMode && savedFieldValuesChanged,
        });
    }

    onRemove(removedRequest) {
        const { details } = this.state;
        const userIdToRemove = removedRequest.value;

        // CREATE event
        const filteredSignatureRequests = this.state.details.signatureRequests.filter(request => request.value !== userIdToRemove);
        if (filteredSignatureRequests.length < this.state.details.signatureRequests.length) {
            details['signatureRequests'] = filteredSignatureRequests;
            this.setState({
                details,
            }, () => this.onEditHandler());
        }

        // UPDATE event
        if (!this.state.pendingSignatureRequests) {
            return;
        }

        const removedPendingSignatureRequest = this.state.pendingSignatureRequests.find(request => request.requestedFrom.id === userIdToRemove);
        if (removedPendingSignatureRequest) {
            const signatureRequestsToCancel = [...this.state.details.signatureRequestsToCancel, removedPendingSignatureRequest.id];
            details['signatureRequestsToCancel'] = signatureRequestsToCancel;
            this.setState({
                details,
            }, () => this.onEditHandler());
        }
    }

    renderUserMultiSelect() {
        const {
            details,
            requiresSignature,
        } = this.state;
        const {
            activeEvent,
            monitors,
            internalUsers,
        } = this.props;

        const { signatureRequests = [] } = activeEvent;
        let pendingSignatureRequests = lodash.filter(signatureRequests, { status: 'PENDING' });
        pendingSignatureRequests = pendingSignatureRequests.filter(pendingRequest => !this.state.details.signatureRequestsToCancel.some(taskId => taskId === pendingRequest.id));
        const pendingRequestedFromUserIds = lodash.map(pendingSignatureRequests, pendingRequest => pendingRequest.requestedFrom.id);
        const formattedPendingSignatureRequests = formatSignaturesForMultiselectCheckbox(pendingSignatureRequests);
        const internalUsersWithoutMonitors = lodash.differenceWith(internalUsers, monitors, (first, second) => first.id === second.id);
        const formattedMonitors = monitors.map(formatItemBy('id', 'displayName'));
        const formattedInternalUsers = internalUsersWithoutMonitors.map(formatItemBy('id', 'displayName'));
        const filteredMonitors = lodash.filter(formattedMonitors, monitor => !lodash.includes(pendingRequestedFromUserIds, monitor.value));
        const filteredInternalUsers = lodash.filter(formattedInternalUsers, internalUser => !lodash.includes(pendingRequestedFromUserIds, internalUser.value));

        const groupedMultiselectUsers = [{
            label: 'Monitors',
            options: filteredMonitors,
        }, {
            label: 'Staff',
            options: filteredInternalUsers,
        },
        ];

        const multiselectComponent = requiresSignature
            ? <UserMultiSelect
                setMultiselectValue={this.setFieldValue.bind(this, 'signatureRequests')}
                multiselectUsers={groupedMultiselectUsers}
                details={details}
                existingPendingSigs={formattedPendingSignatureRequests}
                onRemove={this.onRemove.bind(this)}
            />
            : null;

        return multiselectComponent;
    }

    renderEditNote() {
        const {
            details,
            editNoteCanBeProvided,
        } = this.state;

        const {
            eventHistory,
        } = this.props;

        if (!editNoteCanBeProvided) {
            return null;
        }

        const eventIsSigned = eventHistory.some(history => history.type === 'signed');
        return <DetailInput
            key={'edit-note'}
            title={'Edit Note'}
            placeholder={'The reason for the change is ...'}
            type={'textarea'}
            required={eventIsSigned}
            value={details.editNote}
            onChange={(e) => this.setFieldValue('editNote', e.target.value)}
            data-cy="renderEditNote"
        />;
    }

    getEventTypeFields() {
        const { details } = this.state;
        const { monitors, icfLanguages } = this.props;

        const eventTypeName = lodash.get(details, 'eventType.displayName', '');
        if (eventTypeName === '') {
            return null;
        }

        const eventTypeFields = EventTypeFieldsMap[details.eventType.name];
        if (eventTypeFields) {
            return eventTypeFields({
                updateFieldValue: this.setFieldValue.bind(this),
                details,
                monitors,
                icfLanguages,
            });
        } else {
            return <CustomEventFields
                details={details}
                eventType={details.eventType}
                updateFieldValue={this.setFieldValue.bind(this)}
            />;
        }
    }

    renderSelectedEventType() {
        const {
            requiresSignature,
            details,
        } = this.state;

        const {
            showHistory,
            updateMode,
        } = this.props;

        const hasSelectedEventType = details.eventType && details.eventType.displayName !== '';
        if (!updateMode && !hasSelectedEventType) {
            return null;
        }

        const historyButton = updateMode ? <span className={styles.eventModalToggle} onClick={showHistory}>View History</span> : null;
        const multiselectComponent = this.renderUserMultiSelect();

        const eventFields = this.getEventTypeFields();
        const eventTypeName = lodash.get(details, 'eventType.displayName');

        const headerComponent = updateMode ? <EventDetailsHeader historyButton={historyButton} eventType={eventTypeName} /> : null;

        return (
            <div data-cy="renderSelectedEventType">
                {headerComponent}
                <p className={styles.panelSubhead}>Event Details</p>
                {eventFields}
                {this.renderEditNote()}
                <p className={styles.panelSubhead}>Signatures</p>
                <RequiresSignatureCheckbox
                    requiresSignature={requiresSignature}
                    onToggleCheckbox={this.toggleRequiresSignatureTextBox.bind(this)}
                />
                {multiselectComponent}
            </div>
        );
    }

    getSelectedEventTypeValue() {
        const { eventTypes } = this.props;
        const { details } = this.state;
        const eventTypeName = lodash.get(details, 'eventType.displayName', '');

        const fullEventTypeObject = getEventTypeFromList(eventTypes, eventTypeName);
        const selectedValue = fullEventTypeObject ? fullEventTypeObject.name : '';
        return selectedValue;
    }

    getFormattedEventTypes() {
        const { enabledEventTypes } = this.props;
        const formattedEventTypes = enabledEventTypes.map(formatItemBy('name', 'displayName'));
        return formattedEventTypes;
    }

    renderEventTypeDropdown() {
        if (this.props.updateMode) {
            return null;
        }

        const selectedValue = this.getSelectedEventTypeValue();
        const formattedEventTypes = this.getFormattedEventTypes();

        const selectValue = lodash.find(formattedEventTypes, { value: selectedValue });
        return (
            <InputField
                id={uuid.v4()}
                name="Event Type"
                value={selectValue}
                placeholder="Select an Event type"
                dataType='small_text'
                inputFieldType='single-select'
                isAutoFocused={false}
                onValueChange={this.updateSelectedEventType.bind(this)}
                populateDropDownList={() => formattedEventTypes}
                dataSources={formattedEventTypes}
                required={false}
            />
        );
    }

    renderEditButtons() {
        const { activeEvent: { completedSignatureRequests }, hasManageEvents } = this.props;
        const style = completedSignatureRequests !== 0 ? { width: '100%' } : null;
        return (
            <div className={styles.buttons}>
                {
                    completedSignatureRequests === 0 && hasManageEvents &&
                    <button className={styles.delete_event_button} onClick={this.props.onDelete}>Delete Event</button>
                }
                <div className={styles.editButtons}  style={style}>
                    <Button
                        color={BUTTON.COLOR.BLACK}
                        text={'Cancel'}
                        bgcolor={BUTTON.BGCOLOR.GREY}
                        variant="outlined"
                        size={BUTTON.SIZE.SMALL}
                        outline={BUTTON.OUTCOLOR.GREY}
                        type={BUTTON.SIZE.SECONDARY}
                        onClick={this.onClose.bind(this)}
                        style={{ fontSize: '16px' }}
                    />
                    <Button
                        color={BUTTON.COLOR.WHITE}
                        text={'Save'}
                        bgcolor={BUTTON.BGCOLOR.ORANGE}
                        size={BUTTON.SIZE.SMALL}
                        type={BUTTON.SIZE.SECONDARY}
                        onClick={this.saveEvent.bind(this)}
                        style={{ fontSize: '16px' }}
                    />
                </div>
            </div>
        );
    }

    updateSelectedEventType(targetId, eventDetail) {
        const { eventTypes } = this.props;
        const eventType = lodash.find(eventTypes, eventType => eventType.name === lodash.get(eventDetail, 'value'));
        this.setFieldValue('eventType', eventType);
    }

    canSaveChanges() {
        const { details } = this.state;
        const createMode = !this.props.updateMode;
        const updateMode = this.props.updateMode;
        const isCustomEvent = details.eventType ? details.eventType.name !== 'monitorVisit' && details.eventType.name !== 'icfVersionChange' : false;
        const isCustomEventValid = isCustomEvent ? this.validateCustomEvent(details).isValid : false;

        if (isCustomEvent && !isCustomEventValid) {
            return false;
        }

        if (createMode) {
            const hasSelectedEventType = details.eventType && details.eventType.displayName !== '';
            return hasSelectedEventType;
        } else if (updateMode) {
            const eventDetailsModified = this.state.isEdited;
            const editNoteIsFilledOut = this.state.details.editNote && this.state.details.editNote.length !== 0;

            const editNoteIsRequired = this.props.eventHistory.some(history => history.type === 'signed');
            const requiredFieldsProvided = !this.state.editNoteCanBeProvided || (this.state.editNoteCanBeProvided && !editNoteIsRequired) || (this.state.editNoteCanBeProvided && editNoteIsRequired && editNoteIsFilledOut);
            return eventDetailsModified && requiredFieldsProvided;
        }
    }

    validateCustomEvent(eventData) {
        const { activeEventType } = this.props;

        if (lodash.isEmpty(activeEventType)) {
            return true;
        }

        const formState = { isValid: true, errors: {} };
        const addError = (name, message) => {
            formState.isValid = false;
            formState.errors[name] = message;
        };

        for (const field of activeEventType.fields.filter(f => f.enableOtherOption && lodash.has(eventData, f.name))) {
            const fieldValue = eventData[field.name];
            if (lodash.isEmpty(fieldValue)) {
                addError(field.name, 'required');
            }
        }
        return formState;
    }

    render() {
        const {
            updateMode,
        } = this.props;

        const modeText = updateMode ? 'Save Changes' : 'Add Event';
        const cancelModeText = updateMode ? 'Cancel Changes' : 'Cancel';
        const eventTypeDropdown = this.renderEventTypeDropdown();
        const selectedEventType = this.renderSelectedEventType();

        return (
            <div>
                <div data-cy="validateCustomEvent">
                    {eventTypeDropdown}
                    {selectedEventType}
                    {!updateMode && (
                        <div className={styles.buttons}>
                            <button className={styles.eventsCancel__button}
                                onClick={this.onClose.bind(this)}
                                data-cy="validateCustomEvent-onClose">
                                {cancelModeText}
                            </button>
                            <button className={styles.eventsAdd__button}
                                onClick={this.saveEvent.bind(this)}
                                disabled={!this.canSaveChanges()}
                                data-cy="validateCustomEvent-onSave">
                                {modeText}
                            </button>
                        </div>
                    )}
                </div>
                {updateMode && this.renderEditButtons()}
            </div>
        );
    }
}
